import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
    GroupInspection,
    GroupInspectionsService,
    GroupResponseDto,
    SubgroupResponseDto,
    SubgroupMergeDto,
    GroupByOption,
    GroupInspectionFormData,
    SortOption,
    FormListDto,
    GroupInspectionFormDataResponse,
    SubgroupCreateRequestDto,
    SubgroupEditRequestDto,
    SubgroupMergeRequestDto,
} from '@core/interfaces/engin/mobile-admin/group-inspections';
import {GroupInspectionsApi} from '@core/backend/engin/api/mobile-admin/group-inspections.api';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    APIResponse,
    TableFilterConfig,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    PagingResponse,
    FilterFieldOption,
    Filter,
} from '@core/interfaces/system/system-common';

@Injectable()
export class GroupInspectionsServiceImpl extends GroupInspectionsService {
    constructor(private api: GroupInspectionsApi) {
        super();
    }

    /**
     * Gets the group inspection data with pagination
     * @returns Observable of ServerDataSource for the group inspection data
     */
    getGroupInspectionData(): Observable<DataSource> {
        return this.api.getGroupInspectionData();
    }

    /**
     * Toggles the publish status of a group inspection
     * @param groupId The ID of the group inspection
     * @param published The new published status
     * @returns Observable of GroupResponseDto
     */
    togglePublishStatus(groupId: number, published: boolean): Observable<GroupResponseDto> {
        return this.api.togglePublishStatus(groupId, published);
    }

    /**
     * Gets the list of subgroups for a group inspection
     * @param groupId The ID of the group inspection
     * @returns Observable of array of SubgroupResponseDto
     */
    listSubgroups(groupId: number): Observable<SubgroupResponseDto[]> {
        return this.api.listSubgroups(groupId);
    }

    /**
     * Creates a new group inspection
     * @param data The group inspection data to create
     * @returns Observable of GroupResponseDto
     */
    createGroupInspection(data: GroupInspectionFormData): Observable<APIResponse<GroupInspection>> {
        return this.api.createGroupInspection(data);
    }

    /**
     * Edits an existing group inspection
     * @param groupId The ID of the group inspection
     * @param data The updated group inspection data
     * @returns Observable of GroupResponseDto
     */
    editGroupInspection(groupId: number, data: Partial<GroupInspection>): Observable<GroupResponseDto> {
        return this.api.editGroupInspection(groupId, data);
    }

    /**
     * Deletes a group inspection
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    deleteGroupInspection(groupId: number): Observable<void> {
        return this.api.deleteGroupInspection(groupId);
    }

    /**
     * Refreshes all groups
     * @returns Observable of void
     */
    refreshAllGroups(): Observable<void> {
        return this.api.refreshAllGroups();
    }

    /**
     * Refreshes a specific group
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    refreshGroup(groupId: number): Observable<void> {
        return this.api.refreshGroup(groupId);
    }

    /**
     * Saves the configuration for a group inspection
     * Commits configuration and updates status to READY_FOR_PUBLISH or HAS_WARNINGS
     * @param groupId The ID of the group inspection
     * @returns Observable of GroupResponseDto
     */
    saveConfiguration(groupId: number): Observable<GroupResponseDto> {
        return this.api.saveConfiguration(groupId);
    }

    /**
     * Creates a new subgroup inside the given inspection group
     * @param groupId The ID of the group inspection
     * @param data The subgroup data to create
     * @returns Observable of SubgroupResponseDto
     */
    createSubgroup(groupId: number, data: SubgroupCreateRequestDto): Observable<APIResponse<SubgroupResponseDto[]>> {
        return this.api.createSubgroup(groupId, data);
    }

    /**
     * Update subgroup inside the given inspection group
     * @param groupId The ID of the group inspection
     * @param data The subgroup data to create
     * @returns Observable of SubgroupResponseDto
     */
    updateSubgroup(groupId: number, data: SubgroupEditRequestDto): Observable<APIResponse<SubgroupResponseDto[]>> {
        return this.api.updateSubgroup(groupId, data);
    }

    /**
     * Merges multiple subgroups into one
     * @param groupId The ID of the group inspection
     * @param data The merge data containing target and source subgroups
     * @returns Observable of void
     */
    mergeSubgroups(groupId: number, data: SubgroupMergeRequestDto): Observable<APIResponse<SubgroupResponseDto[]>> {
        return this.api.mergeSubgroups(groupId, data);
    }

    /**
     * Removes a group only if it's in DRAFT status
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    deleteDraftGroup(groupId: number): Observable<void> {
        return this.api.deleteDraftGroup(groupId);
    }

    /**
     * Checks if the given group label already exists
     * @param groupLabel The group label to verify
     * @returns Observable of boolean (true if available, false if exists)
     */
    verifyGroupLabelAvailability(groupLabel: string): Observable<APIResponse<boolean>> {
        return this.api.verifyGroupLabelAvailability(groupLabel);
    }
    /**
     * Get available filter configurations
     */
    getGenericFilters(): Observable<APIResponse<TableFilterConfig[]>> {
        return this.api.getGenericFilters();
    }

    /**
     * Get filter field options dynamically
     * @param body - Request body with field key and filter params
     */
    getFilterFieldOptions(body: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.getFilterFieldOptions(body);
    }

    /**
     * Get available icons for selection
     * Returns array of icon IDs that can be used for group inspection labels
     * @returns Observable of APIResponse containing array of icon ID strings
     */
    getAvailableIcons(): Observable<APIResponse<string[]>> {
        return this.api.getAvailableIcons();
    }

    /**
     * Get group-by options (asset class grouping options)
     * Returns available grouping field options for organizing group inspections
     * @returns Observable of APIResponse containing array of GroupByOption
     */
    getGroupByOptions(): Observable<APIResponse<GroupByOption[]>> {
        return this.api.getGroupByOptions();
    }

    /**
     * Get group inspection details by ID
     * Retrieves full details including filters, forms, and configuration for editing
     * @param groupId - Group inspection ID
     * @returns Observable of APIResponse containing GroupInspectionFormDataResponse
     */
    getGroupInspection(groupId: number): Observable<APIResponse<GroupInspectionFormDataResponse>> {
        return this.api.getGroupInspection(groupId);
    }

    /**
     * Update existing group inspection
     * Updates group inspection details including label, description, icon, scope, and forms
     * @param groupId - Group inspection ID
     * @param data - Updated form data
     * @returns Observable of APIResponse containing updated GroupInspection
     */
    updateGroupInspection(groupId: number, data: GroupInspectionFormData): Observable<APIResponse<GroupInspection>> {
        return this.api.updateGroupInspection(groupId, data);
    }

    /**
     * Get available sort options for subgroup content
     * Returns sorting options that can be applied to assets within a subgroup
     * @param groupInspectionId - The ID of the group inspection
     * @param subgroupId - The ID of the subgroup
     * @returns Observable of APIResponse containing array of SortOption
     */
    getSortOptions(groupInspectionId: number, subgroupId: number): Observable<APIResponse<SortOption[]>> {
        return this.api.getSortOptions(groupInspectionId, subgroupId);
    }

    /**
     * Get subgroup options for a specific group inspection
     * Returns available grouping options that can be used to create subgroups
     * @param groupInspectionId - The ID of the group inspection
     * @returns Observable of APIResponse containing array of GroupByOption
     */
    getSubgroupOptions(groupInspectionId: number): Observable<APIResponse<GroupByOption[]>> {
        return this.api.getSubgroupOptions(groupInspectionId);
    }

    /**
     * Gets a ServerDataSource configured for inspection forms data
     * Returns all available published inspection forms that can be assigned to groups
     * @param groupInspectionId - Optional group inspection ID to filter forms
     * @param subgroupName - Optional subgroup name to filter forms
     * @returns Observable of DataSource for the inspection forms table
     */
    getInspectionFormsDataSource(groupInspectionId?: number, subgroupName?: string): Observable<DataSource> {
        return this.api.getInspectionFormsDataSource(groupInspectionId, subgroupName);
    }

    /**
     * Gets selected inspection forms data by their IDs
     * Retrieves detailed information for a specific set of forms (used for pre-selection)
     * @param selectedId - Array of form IDs to retrieve
     * @returns Observable of PagingResponse containing FormListDto array
     */
    getSelectedInspectionFormsDataSource(selectedId: number[]): Observable<PagingResponse<FormListDto>> {
        return this.api.getSelectedInspectionFormsDataSource(selectedId);
    }

    /**
     * Get filter options for a specific field with search capability
     * Used for dynamic loading of filter dropdown options based on user search
     * @param fieldKey - The field key to get options for (e.g., 'form_name', 'user_name')
     * @param searchString - Search string to filter options
     * @returns Observable of APIResponse containing array of FilterFieldOption
     */
    public getFilterOptions(fieldKey: string, searchString: string): Observable<APIResponse<FilterFieldOption[]>> {
        return this.api.getFilterOptions(fieldKey, searchString);
    }

    /**
     * Get filtered asset count
     * Returns the total number of assets matching the provided filters
     * @param filterList - Array of filters to apply (optional - empty means count all)
     * @returns Observable of  number
     */
    getAssetCount(filterList: Filter[]): Observable<number> {
        return this.api.getAssetCount(filterList);
    }

    saveGroupConfiguration(groupId: number): Observable<APIResponse<GroupResponseDto[]>> {
        return this.api.saveGroupConfiguration(groupId);
    }

    getGroupLabelList(): Observable<APIResponse<string[]>> {
        return this.api.getGroupLabelList();
    }

    /**
     * Gets all form IDs matching current filters
     * Returns all available form IDs without pagination for "Select All" functionality
     * @returns Observable of APIResponse containing array of form IDs
     */
    getAllFormIds(): Observable<APIResponse<number[]>> {
        return this.api.getAllFormIds();
    }
}
